import React, { useState, useMemo } from 'react';
import {
  Card,
  Box,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  alpha,
  useTheme
} from '@mui/material';
import {
  Phone,
  Email,
  Sms,
  MoreVert,
  ContentCopy,
  Delete,
  LocalOffer,
  Share,
  Replay
} from '@mui/icons-material';
import moment from 'moment';
import firebase from '../../utilities/firebase';
import { StateManager } from '../../utilities/stateManager';
import constants from '../../utilities/constants';
import { manualStatusOptions, isStatusLocked, soldCaption, LOCKED_STATUS_MESSAGE } from '../../utilities/leadStatus';
import { canDeleteLead, deleteLeadDoc, PROTECTED_DELETE_MESSAGE } from '../../utilities/leadDelete';
import { handsetSmsUrl } from '../../pages/LeadsPage/leadSms';

// Consistent per-rep color palette (shared shape with LeadCard.js)
const formatPhone = (phone) => {
  if (!phone) return '';
  const cleaned = `${phone}`.replace(/\D/g, '');
  return cleaned.length === 10 ? cleaned.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3') : phone;
};

const REP_COLORS = (theme) => [
  theme.palette.primary.main,
  theme.palette.success.main,
  theme.palette.info.main,
  theme.palette.error.main,
  theme.palette.secondary.main,
  theme.palette.warning.dark,
  '#2E7D32', '#1976D2', '#7B1FA2', '#C2185B',
  '#00796B', '#5D4037', '#455A64', '#D81B60', '#00897B', '#5E35B1',
];

/**
 * Compact lead card for the Car modal Leads tab. The car is already known from
 * the modal, so this drops LeadCard's car image / car-info column and opens the
 * detail inline (via onOpenDetail) instead of a Blade. Action handlers mirror
 * LeadCard: call stays on the cell dialer, text opens the handset SMS app,
 * email keeps the first-contact template, and each tap logs the contact.
 */
const CarLeadCard = ({ lead, car = {}, onUpdate, onOpenDetail }) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [salesRepMenuAnchor, setSalesRepMenuAnchor] = useState(null);
  const [busy, setBusy] = useState(false);

  const contactAttempts = useMemo(() => {
    const contacts = lead.contacts || [];
    return {
      phone: contacts.filter(c => c.type === 'phone' || c.type === 'call').length,
      email: contacts.filter(c => c.type === 'email').length,
      sms: contacts.filter(c => c.type === 'text').length,
      socialMedia: contacts.filter(c => c.type === 'social media').length,
    };
  }, [lead.contacts]);

  const contactedToday = useMemo(() => {
    const today = moment().format('YYYY/MM/DD');
    const contacts = lead.contacts || [];
    if (lead.phone) {
      return contacts.some(c => c.timestamp?.startsWith(today) && (c.type === 'phone' || c.type === 'text'));
    }
    return contacts.some(c => c.timestamp?.startsWith(today) && c.type === 'email');
  }, [lead.contacts, lead.phone]);

  const salesRepName = useMemo(() => {
    const rep = constants.sales.find(s => s.id === lead.sales_id);
    return rep ? rep.name : 'Unassigned';
  }, [lead.sales_id]);

  const salesRepColor = useMemo(() => {
    if (!lead.sales_id) return theme.palette.grey[500];
    const index = constants.sales.findIndex(s => s.id === lead.sales_id);
    return REP_COLORS(theme)[index % 16] || theme.palette.grey[500];
  }, [lead.sales_id, theme]);

  const discountInfo = useMemo(() => {
    if (!lead.discount || lead.discount === 0) return null;
    const expirationDate = lead.expirationDate;
    const isExpired = expirationDate ? moment(expirationDate).isBefore(moment()) : false;
    const expiresSoon = expirationDate ? moment(expirationDate).isBefore(moment().add(7, 'days')) : false;
    return {
      amount: lead.discount,
      isExpired,
      expiresSoon,
      formattedExpiration: expirationDate ? moment(expirationDate).format('MM/DD/YY') : null,
    };
  }, [lead.discount, lead.expirationDate]);

  const getStatusColor = (status) => {
    const i = constants.lead_statuses.indexOf(status);
    if (i === -1) return theme.palette.grey[500];
    const colors = [
      '#708090',
      theme.palette.primary.main,
      theme.palette.success.main,
      theme.palette.error.main,
      theme.palette.success.main,
      theme.palette.warning.main, // car sold - other buyer
    ];
    return colors[i] || theme.palette.grey[500];
  };

  const daysAgo = useMemo(() => {
    if (!lead.date) return null;
    const d = moment(lead.date).startOf('day');
    if (!d.isValid()) return null;
    return moment().startOf('day').diff(d, 'days');
  }, [lead.date]);

  // --- contact logging (identical to LeadCard) ---
  const logContactAttempt = async (type) => {
    try {
      const newContact = {
        type,
        timestamp: moment().format('YYYY/MM/DD HH:mm'),
        note: `Contact attempt via ${type}`,
        user: StateManager.userName || 'Unknown',
      };
      const updatedContacts = [...(lead.contacts || []), newContact];
      await firebase.firestore().collection('leads').doc(lead.id).update({
        contacts: updatedContacts,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
      lead.contacts = updatedContacts;
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error logging automatic contact:', error);
    }
  };

  const handleCallClick = async (e) => {
    e.stopPropagation();
    if (!lead.phone) return;
    // Cell/desk dialer. Softphone stays on /phone only (SLED-087). CRM does not open it.
    // Open the app before logging: awaiting the write first loses the user gesture.
    window.open(`tel:${lead.phone}`, '_self');
    logContactAttempt('phone');
  };

  const handleEmailClick = async (e) => {
    e.stopPropagation();
    if (!lead.email) return;
    const subject = `${car.year || ''} ${car.make || ''} ${car.model || 'Vehicle'} - ${car.stock || ''} at Skyway Classics`;
    let emailUrl = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}`;
    if (contactAttempts.email === 0) {
      const template = `Hi ${lead.name || 'there'}, this is ${StateManager.userName || 'the Sales Team'} from Skyway Classics. Thanks for reaching out about our ${car.year || ''} ${car.make || ''} ${car.model || 'vehicle'}. Yes it is still available. How can I help you with the vehicle?`;
      emailUrl += `&body=${encodeURIComponent(template)}`;
    }
    // Open the app before logging: awaiting the write first loses the user gesture.
    window.open(emailUrl, '_self');
    logContactAttempt('email');
  };

  const handleSmsClick = (e) => {
    e.stopPropagation();
    if (!lead.phone) return;
    const smsUrl = handsetSmsUrl({
      phone: lead.phone,
      leadName: lead.name,
      car,
      userName: StateManager.userName,
      smsCount: contactAttempts.sms,
    });
    if (!smsUrl) return;
    // Open the app before logging: awaiting the write first loses the user gesture.
    window.open(smsUrl, '_self');
    logContactAttempt('text');
  };

  const handleSocialMediaClick = async (e) => {
    e.stopPropagation();
    await logContactAttempt('social media');
  };

  // --- status / rep updates ---
  const handleStatusChange = async (newStatus) => {
    setBusy(true);
    try {
      await firebase.firestore().collection('leads').doc(lead.id).update({
        status: newStatus,
        status_time: moment().format('YYYY-MM-DD HH:mm:ss'),
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
      lead.status = newStatus;
      lead.status_time = moment().format('YYYY-MM-DD HH:mm:ss');
      StateManager.setAlertAndOpen('Status updated successfully', 'success');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating status:', error);
      StateManager.setAlertAndOpen('Error updating status', 'error');
    } finally {
      setBusy(false);
      setStatusMenuAnchor(null);
    }
  };

  const handleSalesRepChange = async (salesRepId) => {
    setBusy(true);
    try {
      await firebase.firestore().collection('leads').doc(lead.id).update({
        sales_id: salesRepId || null,
        updated_at: firebase.firestore.FieldValue.serverTimestamp(),
      });
      lead.sales_id = salesRepId || null;
      StateManager.setAlertAndOpen('Sales rep updated successfully', 'success');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating sales rep:', error);
      StateManager.setAlertAndOpen('Error updating sales rep', 'error');
    } finally {
      setBusy(false);
      setSalesRepMenuAnchor(null);
    }
  };

  // --- copy / delete ---
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      StateManager.setAlertAndOpen(`${label} copied to clipboard`, 'success');
    } catch (error) {
      StateManager.setAlertAndOpen(`Error copying ${label}`, 'error');
    }
  };

  const handleCopyCarUrl = () => {
    if (!car.stock) {
      StateManager.setAlertAndOpen('No car stock number available', 'error');
      return;
    }
    const title = `${car.year || ''}${car.year ? ' ' : ''}${car.make || ''}${car.make ? ' ' : ''}${car.model || ''}${car.model ? ' ' : ''}${car.trim === 'Base' ? '' : car.trim || ''}`;
    const slug = car.slug || '/' + `${car.stock}-${title}`.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
    copyToClipboard(`https://skywayclassics.com/vehicles${slug}`, 'Car link');
  };

  const handleDeleteLead = async () => {
    setAnchorEl(null);
    try {
      await deleteLeadDoc(firebase.firestore().collection('leads').doc(lead.id));
      StateManager.setAlertAndOpen('Lead deleted successfully', 'success');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting lead:', error);
      StateManager.setAlertAndOpen('Error deleting lead', 'error');
    }
  };

  const actionChipSx = (enabled, color) => ({
    fontSize: '0.8rem',
    height: 32,
    minWidth: 56,
    cursor: enabled ? 'pointer' : 'default',
    opacity: enabled ? 1 : 0.4,
    color: enabled ? undefined : theme.palette.grey[500],
    borderColor: enabled ? undefined : theme.palette.grey[400],
    '&:hover': enabled ? { bgcolor: color, color: 'white' } : {},
  });

  return (
    <Card
      onClick={() => onOpenDetail && onOpenDetail(lead)}
      sx={{
        cursor: 'pointer',
        p: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: theme.shadows[6] },
      }}
    >
      {/* Header: name + date + overflow */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.1rem', lineHeight: 1.2 }} noWrap>
            {lead.name || 'No name'}
          </Typography>
          {(lead.phone || lead.email) && (
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }} noWrap>
              {[formatPhone(lead.phone), lead.email].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
              {lead.date ? moment(lead.date).format('MM/DD/YY') : '—'}
            </Typography>
            {daysAgo !== null && (
              <Typography variant="caption" color="text.secondary">
                {daysAgo === 0 ? 'today' : `${daysAgo}d ago`}
              </Typography>
            )}
          </Box>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Status + rep + source/badges */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5, alignItems: 'center' }}>
        <Chip
          label={isStatusLocked(lead) ? soldCaption(lead) : (lead.status || 'pending contact')}
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            if (isStatusLocked(lead)) {
              StateManager.setAlertAndOpen(LOCKED_STATUS_MESSAGE, 'info');
              return;
            }
            setStatusMenuAnchor(e.currentTarget);
          }}
          sx={{
            bgcolor: alpha(getStatusColor(lead.status), 0.1),
            color: getStatusColor(lead.status),
            border: `1px solid ${alpha(getStatusColor(lead.status), 0.3)}`,
            cursor: 'pointer', fontWeight: 600,
          }}
        />
        <Chip
          label={salesRepName}
          size="small"
          onClick={(e) => { e.stopPropagation(); setSalesRepMenuAnchor(e.currentTarget); }}
          sx={{
            bgcolor: alpha(salesRepColor, 0.1),
            color: salesRepColor,
            border: `1px solid ${alpha(salesRepColor, 0.3)}`,
            cursor: 'pointer',
          }}
        />
        {lead.source && (
          <Chip label={`source: ${lead.source}`} size="small" variant="outlined" color="info" />
        )}
        {lead.reinquiry_count > 0 && (
          <Tooltip
            arrow
            title={
              (lead.reinquiries || []).length
                ? (lead.reinquiries || []).slice(-5).reverse().map((r, i) => (
                    <div key={i}>{(r.date || r.sortDate || '').slice(0, 10)}{r.source ? ` · ${r.source}` : ''}</div>
                  ))
                : 'Re-inquired'
            }
          >
            <Chip
              icon={<Replay fontSize="small" />}
              label={`Reached out ${lead.reinquiry_count + 1}×`}
              size="small"
              sx={{
                bgcolor: alpha(theme.palette.warning.main, 0.1),
                color: theme.palette.warning.dark,
                border: `1px solid ${alpha(theme.palette.warning.main, 0.4)}`,
                fontWeight: 600,
              }}
            />
          </Tooltip>
        )}
        {discountInfo && (
          <Chip
            icon={<LocalOffer fontSize="small" />}
            label={`$${discountInfo.amount}${discountInfo.formattedExpiration ? ` exp ${discountInfo.formattedExpiration}` : ''}`}
            size="small"
            sx={{
              bgcolor: alpha(discountInfo.isExpired ? theme.palette.error.main : discountInfo.expiresSoon ? theme.palette.warning.main : theme.palette.success.main, 0.1),
              color: discountInfo.isExpired ? theme.palette.error.main : discountInfo.expiresSoon ? theme.palette.warning.dark : theme.palette.success.dark,
              fontWeight: 600,
            }}
          />
        )}
        {contactedToday ? (
          <Chip
            label="Contacted Today"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.success.main, 0.1),
              color: theme.palette.success.dark,
              border: `1px solid ${alpha(theme.palette.success.main, 0.3)}`,
            }}
          />
        ) : (
          <Chip
            label="Needs Contact"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`,
            }}
          />
        )}
      </Box>

      {/* Comments */}
      {lead.comments && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontSize: '0.8rem', fontStyle: 'italic', lineHeight: 1.3, mt: 1.5,
            overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box',
            WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
          }}
        >
          "{lead.comments}"
        </Typography>
      )}

      {/* Quick contact actions (auto-logged) */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1.5 }}>
        <Tooltip title={lead.phone ? `Call ${lead.name || 'lead'}` : 'No phone'} arrow>
          <Chip
            icon={<Phone fontSize="small" />}
            label={contactAttempts.phone}
            size="small"
            variant="outlined"
            color={lead.phone ? 'primary' : 'default'}
            onClick={lead.phone ? handleCallClick : undefined}
            sx={actionChipSx(!!lead.phone, theme.palette.primary.main)}
          />
        </Tooltip>
        <Tooltip title={lead.phone ? `Text ${lead.name || 'lead'}` : 'No phone'} arrow>
          <Chip
            icon={<Sms fontSize="small" />}
            label={contactAttempts.sms}
            size="small"
            variant="outlined"
            color={lead.phone ? 'info' : 'default'}
            onClick={lead.phone ? handleSmsClick : undefined}
            sx={actionChipSx(!!lead.phone, theme.palette.info.main)}
          />
        </Tooltip>
        <Tooltip title={lead.email ? `Email ${lead.name || 'lead'}` : 'No email'} arrow>
          <Chip
            icon={<Email fontSize="small" />}
            label={contactAttempts.email}
            size="small"
            variant="outlined"
            color={lead.email ? 'secondary' : 'default'}
            onClick={lead.email ? handleEmailClick : undefined}
            sx={actionChipSx(!!lead.email, theme.palette.secondary.main)}
          />
        </Tooltip>
        <Tooltip title="Log social media contact" arrow>
          <Chip
            icon={<Share fontSize="small" />}
            label={contactAttempts.socialMedia}
            size="small"
            variant="outlined"
            onClick={handleSocialMediaClick}
            sx={{ fontSize: '0.8rem', height: 32, minWidth: 56, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          />
        </Tooltip>
        <Tooltip title={car.stock ? 'Copy car URL' : 'No car info'} arrow>
          <Chip
            icon={<ContentCopy fontSize="small" />}
            label="Link"
            size="small"
            variant="outlined"
            onClick={car.stock ? (e) => { e.stopPropagation(); handleCopyCarUrl(); } : undefined}
            data-sold-ok
            sx={{
              fontSize: '0.8rem', height: 32, minWidth: 56,
              cursor: car.stock ? 'pointer' : 'default',
              opacity: car.stock ? 1 : 0.4,
              color: car.stock ? '#2E7D32' : theme.palette.grey[500],
              borderColor: car.stock ? '#2E7D32' : theme.palette.grey[400],
              '& .MuiChip-icon': { color: car.stock ? '#2E7D32' : theme.palette.grey[500] },
              '&:hover': car.stock ? { bgcolor: '#2E7D32', color: 'white', '& .MuiChip-icon': { color: 'white' } } : {},
            }}
          />
        </Tooltip>
      </Box>

      {/* Overflow menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} PaperProps={{ sx: { minWidth: 200 } }}>
        <MenuItem onClick={() => { copyToClipboard(lead.phone, 'Phone number'); setAnchorEl(null); }} disabled={!lead.phone}>
          <ListItemIcon><ContentCopy /></ListItemIcon>
          <ListItemText>Copy phone number</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { copyToClipboard(lead.email, 'Email address'); setAnchorEl(null); }} disabled={!lead.email}>
          <ListItemIcon><ContentCopy /></ListItemIcon>
          <ListItemText>Copy email</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { handleCopyCarUrl(); setAnchorEl(null); }} disabled={!car.stock}>
          <ListItemIcon><ContentCopy /></ListItemIcon>
          <ListItemText>Copy car URL</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteLead} disabled={!canDeleteLead(lead, StateManager)} sx={{ color: 'error.main' }}>
          <ListItemIcon><Delete sx={{ color: 'error.main' }} /></ListItemIcon>
          <ListItemText
            primary="Delete lead"
            secondary={canDeleteLead(lead, StateManager) ? null : PROTECTED_DELETE_MESSAGE}
          />
        </MenuItem>
      </Menu>

      {/* Status menu */}
      <Menu anchorEl={statusMenuAnchor} open={Boolean(statusMenuAnchor)} onClose={() => setStatusMenuAnchor(null)} PaperProps={{ sx: { minWidth: 180 } }}>
        {manualStatusOptions(lead.status).map((status) => (
          <MenuItem
            key={status}
            disabled={busy}
            onClick={(e) => { e.stopPropagation(); handleStatusChange(status); }}
            sx={{ color: getStatusColor(status), '&:hover': { bgcolor: alpha(getStatusColor(status), 0.1) } }}
          >
            <ListItemIcon>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getStatusColor(status) }} />
            </ListItemIcon>
            <ListItemText>{status.charAt(0).toUpperCase() + status.slice(1)}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      {/* Sales rep menu */}
      <Menu anchorEl={salesRepMenuAnchor} open={Boolean(salesRepMenuAnchor)} onClose={() => setSalesRepMenuAnchor(null)} PaperProps={{ sx: { minWidth: 200 } }}>
        <MenuItem
          disabled={busy}
          selected={!lead.sales_id}
          onClick={(e) => { e.stopPropagation(); handleSalesRepChange(''); }}
          sx={{ color: theme.palette.grey[500] }}
        >
          <ListItemText>Unassigned</ListItemText>
        </MenuItem>
        {constants.sales.map((rep, index) => {
          const repColor = REP_COLORS(theme)[index % 16] || theme.palette.grey[500];
          return (
            <MenuItem
              key={rep.id}
              disabled={busy}
              selected={lead.sales_id === rep.id}
              onClick={(e) => { e.stopPropagation(); handleSalesRepChange(rep.id); }}
              sx={{ color: repColor, '&:hover': { bgcolor: alpha(repColor, 0.1) } }}
            >
              <ListItemText>{rep.name}</ListItemText>
            </MenuItem>
          );
        })}
      </Menu>
    </Card>
  );
};

export default React.memo(CarLeadCard);
